<script>
  import { onDestroy } from "svelte";
  import { getHotspotData } from "./utils.svelte.js";
  import easyload from "../assets/img/easyload.png";
  import kazang from "../assets/img/kazang.png";
  import foryou from "../assets/img/foryou.png";
  import toomuchwifi from "../assets/img/toomuchwifi.png";
  import ott from "../assets/img/ott.png";
  import hotspotLogo from "../assets/img/hotspot.png";
  import back from "../assets/img/back.png";
  import NoVoucher from "./components/NoVoucher.svelte";
  import Gap from "./components/Gap.svelte";
  import LoadingIndicator from "./components/LoadingIndicator.svelte";
  import Error from "./components/Error.svelte";
  import { logVoucherMissingMac } from "./utils.svelte.js";

  export let store;

  let isLoading = false;
  let disposed = false;

  let vendorSelectionMode = true;

  // Digit lengths from cloudportal voucher routing
  // (easyload=14, 1foryou=16, kazang/tmwf/ott=12).
  const voucherVendors = [
    {
      id: "easyload",
      label: "EasyLoad / Shop2Shop",
      logo: easyload,
      digits: 14,
    },
    {
      id: "kazang",
      label: "Kazang",
      logo: kazang,
      digits: 12,
    },
    {
      id: "1foryou",
      label: "1ForYou",
      logo: foryou,
      digits: 16,
    },
    {
      id: "toomuchwifi",
      label: "TooMuchWifi",
      logo: toomuchwifi,
      digits: 12,
    },
    {
      id: "ott",
      label: "OTT",
      logo: ott,
      digits: 12,
    },
  ];

  let selectedVendor = null;

  let voucherCode = null;
  //to be used when endpoint to redeem voucher accepts vounder
  let allocationTo = null;
  let errorMessage = null;
  let errorMessageDetails = null;
  let errorTimeout = null;
  const portalURL = import.meta.env.VITE_BASE_PORTAL_URL;

  function hideError() {
    if (errorTimeout) {
      clearTimeout(errorTimeout);
    }
    errorTimeout = setTimeout(() => {
      errorMessage = null;
      errorMessageDetails = null;
    }, 8000);
  }

  function cleanedVoucherCode() {
    return String(voucherCode || "").replace(/\D/g, "");
  }

  function vendorsForDigits(digitCount) {
    return voucherVendors
      .filter((vendor) => vendor.digits === digitCount)
      .map((vendor) => vendor.label);
  }

  function digitMismatchMessage(enteredDigits) {
    const matches = vendorsForDigits(enteredDigits);
    if (matches.length > 0) {
      return `You entered ${enteredDigits} digits, but ${selectedVendor.label} uses ${selectedVendor.digits}. Try ${matches.join(" or ")}.`;
    }
    return `You entered ${enteredDigits} digits, but ${selectedVendor.label} vouchers use ${selectedVendor.digits} digits.`;
  }

  function setSelectedVendor(vendor) {
    selectedVendor = voucherVendors.find((entry) => entry.id === vendor) || null;
    voucherCode = null;
    errorMessage = null;
    errorMessageDetails = null;
    vendorSelectionMode = false;
  }

  async function redeemVoucher() {
    if (isLoading || disposed) return;
    errorMessage = null;
    errorMessageDetails = null;

    if (!selectedVendor) {
      errorMessage = "Please select a vendor.";
      hideError();
      return;
    }

    const cleanedCode = cleanedVoucherCode();
    if (!cleanedCode) {
      errorMessage = "Please enter a voucher code.";
      hideError();
      return;
    }

    if (cleanedCode.length !== selectedVendor.digits) {
      errorMessage = "Voucher length does not match this supplier";
      errorMessageDetails = digitMismatchMessage(cleanedCode.length);
      return;
    }

    isLoading = true;

    try {
      // Best effort: unknown node context must not block voucher redemption.
      // Refresh before submission to avoid reporting a node from before roaming.
      let originNode = null;
      try {
        await getHotspotData();
        if (store.isConnectedToHotspot) originNode = store.node || null;
      } catch {
        // Retain existing redemption behaviour if optional diagnostics fail.
      }
      if (disposed) return;
      const macAddress = store.isConnectedToHotspot
        ? store.macAddress?.trim()
        : null;
      if (!macAddress) {
        logVoucherMissingMac({
          voucherVendor: selectedVendor.id,
          isConnectedToHotspot: store.isConnectedToHotspot,
          hasStoredMac: Boolean(store.macAddress),
          node: store.node,
        });
        errorMessage = "We couldn't identify your device yet.";
        errorMessageDetails =
          "Refresh the page and try again. If it still fails, disconnect and reconnect to SuperZone.";
        hideError();
        return;
      }
      let response = await fetch(`${portalURL}hotspot/voucher/device/`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          voucher_vendor: selectedVendor.id,
          voucher: cleanedCode,
          cellphone: store.cellphone,
          mac_address: macAddress,
          is_connected_to_hotspot: store.isConnectedToHotspot,
          origin_nas_identifier: originNode,
        }),
      });

      if (disposed) return;
      if (response.status === 200) {
        store.setState("ACCOUNT");
      } else {
        let data = null;
        try {
          data = await response.json();
        } catch {
          data = null;
        }
        if (disposed) return;
        const backendMessage =
          data?.message || data?.detail || "Error redeeming voucher";
        errorMessage = backendMessage;
        // Only add a generic hint when the backend did not explain the failure.
        if (
          !data?.message &&
          !data?.detail
        ) {
          errorMessageDetails =
            "Please make sure you have a valid voucher and try again";
        }
        hideError();
      }
    } catch (error) {
      if (disposed) return;
      errorMessage = "Error redeeming voucher";
      errorMessageDetails =
        "Please check your connection and try again";
      hideError();
    } finally {
      isLoading = false;
    }
  }

  function backButton() {
    if (isLoading) return;
    errorMessage = null;
    errorMessageDetails = null;
    if (vendorSelectionMode === false) {
      vendorSelectionMode = true;
      selectedVendor = null;
      voucherCode = null;
    } else {
      store.setState("ACCOUNT");
    }
  }

  $: cleanedCode = String(voucherCode || "").replace(/\D/g, "");
  $: enteredDigits = cleanedCode.length;
  $: digitMismatch =
    selectedVendor != null &&
    enteredDigits > 0 &&
    enteredDigits !== selectedVendor.digits;
  onDestroy(() => {
    disposed = true;
    clearTimeout(errorTimeout);
  });
</script>

<div class="flex flex-col items-center justify-center mx-4 w-full px-2 py-6">
  <div class="w-full">
    <button class="flex flex-row" on:click={() => backButton()} disabled={isLoading}>
      <img class="w-3" src={back} alt="Back button" />
      <div class="w-1"></div>
      <p class="text-[#5b95c8] text-md font-bold mx-2">Back</p>
    </button>
  </div>

  <!-- Logo Section -->
  <div class="flex justify-center w-full mb-4">
    <img src={hotspotLogo} alt="TooMuchWifi Hotspot" class="w-2/5" />
  </div>

  <Gap h="6" />

  {#if isLoading}
    <LoadingIndicator width="150px" />
  {:else}
    {#if vendorSelectionMode === true}
      <div class="flex flex-col w-full">
        <p class="font-medium text-sm text-center">
          What type of hotspot voucher do you have?
        </p>
        <p class="mt-1 text-center text-[11px] text-gray-500">
          Match the supplier and digits on your voucher
        </p>
        <p class="mt-1 text-center text-[11px] text-amber-700">
          For monthly payments please load your voucher at
          <a
            href="https://portal.toomuchwifi.co.za/load-voucher/"
            class="underline font-semibold"
            target="_blank"
            rel="noopener noreferrer"
          >
            portal.toomuchwifi.co.za
          </a>
        </p>

        <Gap h="4" />

        <div class="grid grid-cols-3 gap-2 max-w-md mx-auto w-full">
          {#each voucherVendors as vendor}
            <button
              type="button"
              on:click={() => setSelectedVendor(vendor.id)}
              class="rounded-md border border-gray-200 bg-white px-1.5 py-2 shadow-sm cursor-pointer flex flex-col items-center justify-start gap-1"
            >
              <img
                src={vendor.logo}
                alt={`${vendor.label} voucher`}
                class="h-7 w-11 object-contain"
              />
              <span
                class="text-[10px] font-semibold text-gray-800 leading-tight text-center"
              >
                {vendor.label}
              </span>
              <span class="text-[10px] text-gray-500 leading-none">
                {vendor.digits} digits
              </span>
            </button>
          {/each}
        </div>
      </div>

      <Gap h="4" />

      {#if errorMessage}
        <Error message={errorMessage} details={errorMessageDetails}></Error>
      {/if}

      <Gap h="8" />

      <NoVoucher {store} />
    {/if}

    {#if vendorSelectionMode === false && selectedVendor != null}
      <div class="flex flex-col items-center justify-center max-w-4xl py-6 w-full">
        <p class="font-medium w-full text-md">
          Enter your {selectedVendor.label} voucher code
        </p>

        <Gap h="6" />

        <div class="border border-gray-300 flex flex-row rounded-md w-full">
          <button
            type="button"
            on:click={() => backButton()}
            class="bg-white rounded-tl-md rounded-bl-md border-r-[1px] border-gray-400 shrink-0"
          >
            <div class="p-2">
              <img
                src={selectedVendor.logo}
                alt={`${selectedVendor.label} logo`}
                class="max-h-10 max-w-14 object-contain"
              />
            </div>
          </button>
          <input
            type="tel"
            id="pin"
            required
            maxlength="20"
            inputmode="numeric"
            placeholder={`${selectedVendor.digits}-digit code`}
            class="px-2 py-5 rounded-tr-md rounded-br-md w-full min-w-0"
            bind:value={voucherCode}
          />
        </div>

        {#if digitMismatch}
          <div class="w-full mt-3">
            <Error
              message="Voucher length does not match this supplier"
              details={digitMismatchMessage(enteredDigits)}
            ></Error>
          </div>
        {/if}

        <Gap h="6" />

        <button
          type="button"
          on:click={() => redeemVoucher()}
          disabled={digitMismatch || enteredDigits === 0}
          class="bg-[#009946] py-5 text-white font-bold rounded-md w-full disabled:opacity-50 disabled:cursor-not-allowed"
        >
          Connect
        </button>

        {#if errorMessage && !digitMismatch}
          <Gap h="6" />
          <Error message={errorMessage} details={errorMessageDetails}></Error>
        {/if}

        <Gap h="14" />

        <NoVoucher {store} />
      </div>
    {/if}
  {/if}
</div>
